import {
  useCallback,
  useEffect, useRef, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  BOUNDARY_MAX_VALUE_ID, BOUNDARY_MIN_VALUE_ID,
} from '~/boundary/settings/boundarySettings.helpers';
import { areBoundaryTerritoriesNumericValuesValid } from '~/boundary/settings/numeric/boundarySettingsNumeric.helpers';
import { useFetchBoundaryTerritories } from '~/boundary/settings/useFetchBoundaryTerritories';
import { recalculateBoundaryTerritoryColors } from '~/sidebar/sidebarApps/mapTools/boundary/customizeFills/boundaryFillSettings.helpers';
import { useGetBoundaryGroup } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { useBoundaryTerritoryDetailsSelector } from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.selectors';
import { boundaryTerritoryGroupsUpdateRequest } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import type { BoundaryTerritory } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { useDataLayersBTGsSelector } from '~/store/boundaryTerritoryGroups/dataLayersTool/dataLayersBTGs.selectors';
import {
  mapSettingsDataLayersUpdateDataLayer,
  mapSettingsDataLayersUpdateIndividualLayerSettings,
} from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.actionCreators';
import {
  useMapSettingsBoundaryGroupsPerDataLayersSelector,
  useMapSettingsDataLayerGroupsSelector,
  useMapSettingsDataLayersIndividualLayerSettingsSelector,
} from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.selectors';
import type { DataLayerGroupId } from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.state';
import {
  createSampleBTGForDataLayer, DATA_LAYER_TOOL_DEFAULT_NUMBER_OF_RANGES,
  individualLayerSettingsToBTGSettings,
} from './dataLayersTool.helpers';

export const useDataLayersEditViaBTG = (dataLayerGroupId: DataLayerGroupId, demographic?: string | null) => {
  const dispatch = useDispatch();
  const dataLayers = useMapSettingsDataLayerGroupsSelector();
  const boundaryGroupsPerDataLayers = useMapSettingsBoundaryGroupsPerDataLayersSelector();
  const individualLayerSettings = useMapSettingsDataLayersIndividualLayerSettingsSelector();
  const { getBoundaryGroupById } = useGetBoundaryGroup();
  const dataLayersBTGs = useDataLayersBTGsSelector();
  const dataLayerGroup = dataLayers.find(layer => layer.id === dataLayerGroupId);
  const boundaryGroupId = boundaryGroupsPerDataLayers[dataLayerGroupId] ?? 0;

  // #region Individual Layer Settings

  // keep in a ref so reading it doesn't force the reset effect to re-run on every unrelated individual layer settings update after initial load
  const individualLayerSettingsRef = useRef(individualLayerSettings);
  individualLayerSettingsRef.current = individualLayerSettings;

  const getIndividualSettingsFor = useCallback((demographic: Maybe<string>) => {
    const individualSettings = (demographic ? individualLayerSettingsRef.current[boundaryGroupId]?.[demographic] : null);
    return individualSettings ? individualLayerSettingsToBTGSettings(individualSettings) : null;
  }, [boundaryGroupId]);

  const [individualSettingsActive, setIndividualSettingsActive] = useState(
    () => !!getIndividualSettingsFor(demographic)?.individualSettingsActive,
  );

  // demographic changed outside, so we update the individualSettingsActive flag accordingly
  useEffect(() => {
    setIndividualSettingsActive(!!getIndividualSettingsFor(demographic)?.individualSettingsActive);
  }, [demographic, getIndividualSettingsFor]);

  // #endregion Individual Layer Settings

  // #region BTG Handling

  // kept in a ref so reading it doesn't force the reset effect to re-run on every unrelated BTG update after initial load
  const dataLayersBTGsRef = useRef(dataLayersBTGs);
  dataLayersBTGsRef.current = dataLayersBTGs;

  const getCurrentOrSampleBTG = useCallback(() => {
    if (!dataLayerGroup) {
      return null;
    }

    if (!demographic || !individualSettingsActive) {
      return createSampleBTGForDataLayer({ dataLayerGroup, boundaryGroupId });
    }

    const matchingBoundaryTerritoryGroup = dataLayersBTGsRef.current.find(
      btg => btg.settings.connectedEntityData?.id === dataLayerGroupId && btg.settings.demographicId === demographic
    );

    return matchingBoundaryTerritoryGroup ? {
      ...matchingBoundaryTerritoryGroup,
      settings: {
        ...matchingBoundaryTerritoryGroup.settings,
        ...getIndividualSettingsFor(demographic)?.btgSettings,
      },
    } : null;
  }, [boundaryGroupId, dataLayerGroup, dataLayerGroupId, demographic, getIndividualSettingsFor, individualSettingsActive]);

  const [boundaryTerritoryGroup, setBoundaryTerritoryGroup] = useState(getCurrentOrSampleBTG);

  // data layer changed outside, so we reset BTG from the current store state, discarding any active changes
  useEffect(() => {
    setBoundaryTerritoryGroup(getCurrentOrSampleBTG());
  }, [getCurrentOrSampleBTG]);

  // #endregion BTG Handling

  const [isLoading, setIsLoading] = useState(false);

  const updateBoundaryTerritories = useFetchBoundaryTerritories(setBoundaryTerritoryGroup, setIsLoading);

  const onNumberOfRangesChange = useCallback((newNumberOfRanges: number) => {
    if (!boundaryTerritoryGroup) {
      return;
    }

    if (!individualSettingsActive) {
      setBoundaryTerritoryGroup({
        ...boundaryTerritoryGroup,
        settings: {
          ...boundaryTerritoryGroup.settings,
          rangesCount: newNumberOfRanges,
        },
      });
      return;
    }

    const newBoundaryTerritoryGroup = {
      ...boundaryTerritoryGroup,
      settings: {
        ...boundaryTerritoryGroup.settings,
        rangesCount: newNumberOfRanges,
      },
    };

    updateBoundaryTerritories({
      newBoundaryTerritoryGroup,
      originalBoundaryTerritoryGroup: getCurrentOrSampleBTG() ?? newBoundaryTerritoryGroup,
      filter: null,
    });
  }, [boundaryTerritoryGroup, individualSettingsActive, updateBoundaryTerritories, getCurrentOrSampleBTG]);

  const onRecalculateRangesClick = useCallback(() => {
    if (!boundaryTerritoryGroup) {
      return;
    }

    updateBoundaryTerritories({
      newBoundaryTerritoryGroup: boundaryTerritoryGroup,
      originalBoundaryTerritoryGroup: getCurrentOrSampleBTG() ?? boundaryTerritoryGroup,
      filter: null,
    });
  }, [boundaryTerritoryGroup, updateBoundaryTerritories, getCurrentOrSampleBTG]);

  // resets the individual style override back to the data layer's shared style, instead of the global defaults
  const handleRestoreSharedColors = useCallback(() => {
    if (!boundaryTerritoryGroup || !dataLayerGroup) {
      return;
    }

    const sharedStyle = dataLayerGroup.settings.style;

    setBoundaryTerritoryGroup({
      ...boundaryTerritoryGroup,
      settings: {
        ...boundaryTerritoryGroup.settings,
        boundaryTerritories: recalculateBoundaryTerritoryColors(
          boundaryTerritoryGroup.settings.boundaryTerritories, sharedStyle.gradientColors,
        ),
        style: {
          ...boundaryTerritoryGroup.settings.style,
          gradientColors: sharedStyle.gradientColors,
        },
      },
    });
  }, [boundaryTerritoryGroup, dataLayerGroup]);

  // shared mode has no shared style to restore to, so the default global-default-colors behavior is used instead
  const onRestoreDefaultColors = individualSettingsActive ? handleRestoreSharedColors : undefined;

  const onBoundaryTerritoryChange = useCallback((BTs: ReadonlyArray<BoundaryTerritory>) => {
    if (boundaryTerritoryGroup) {
      setBoundaryTerritoryGroup({
        ...boundaryTerritoryGroup,
        settings: {
          ...boundaryTerritoryGroup.settings,
          boundaryTerritories: BTs,
        },
      });
    }
  }, [boundaryTerritoryGroup]);

  const onSubmit = useCallback((params: { onDone?: () => void }) => {
    if (!boundaryTerritoryGroup) {
      return;
    }

    if (individualSettingsActive && !areBoundaryTerritoriesNumericValuesValid(boundaryTerritoryGroup.settings.boundaryTerritories)) {
      return;
    }

    const onDone = () => {
      setIsLoading(false);
      params.onDone?.();
    };

    setIsLoading(true);

    const numberOfRanges = boundaryTerritoryGroup.settings.rangesCount || DATA_LAYER_TOOL_DEFAULT_NUMBER_OF_RANGES;

    // Update individual layer settings for this demographic
    if (demographic) {
      dispatch(mapSettingsDataLayersUpdateIndividualLayerSettings({
        boundaryGroupId,
        demographicId: demographic,
        dataLayerId: dataLayerGroupId,
        settings: {
          individualSettingsActive,
          boundarySettings: individualSettingsActive ? {
            boundaryTerritories: boundaryTerritoryGroup.settings.boundaryTerritories,
            style: boundaryTerritoryGroup.settings.style,
            numberOfRanges,
          } : {},
        },
      }));
    }

    if (individualSettingsActive) {
      dispatch(boundaryTerritoryGroupsUpdateRequest(boundaryTerritoryGroup, { fetchBoundaryDetails: true, onDone }));
    }
    else {
      // Update the data layer settings (shared with all demographics), unless individual settings are active for this layer
      dispatch(mapSettingsDataLayersUpdateDataLayer({
        id: dataLayerGroupId,
        settings: {
          style: boundaryTerritoryGroup.settings.style,
          numberOfRanges,
          disableMapInteraction: boundaryTerritoryGroup.settings.disableMapInteraction,
          hideLabels: boundaryTerritoryGroup.settings.hideLabels,
          labelStyle: boundaryTerritoryGroup.settings.labelStyle,
          labelValueConfig: boundaryTerritoryGroup.settings.labelValueConfig,
        },
      }));

      onDone();
    }
  }, [boundaryTerritoryGroup, individualSettingsActive, demographic, dispatch, boundaryGroupId, dataLayerGroupId]);

  const territoryDetails = useBoundaryTerritoryDetailsSelector();
  const numericalDataForActiveFill = territoryDetails.get(boundaryTerritoryGroup?.boundaryTerritoryGroupId ?? 0)?.numericalData;
  const rangeMin: number | typeof BOUNDARY_MIN_VALUE_ID = numericalDataForActiveFill?.totalLowest ?? BOUNDARY_MIN_VALUE_ID;
  const rangeMax: number | typeof BOUNDARY_MAX_VALUE_ID = numericalDataForActiveFill?.totalHighest ?? BOUNDARY_MAX_VALUE_ID;

  return {
    dataLayerGroup,
    boundaryTerritoryGroup,
    boundaryGroup: getBoundaryGroupById(boundaryGroupId),
    individualSettingsActive,
    rangeMin,
    rangeMax,
    onIndividualSettingsActiveChange: setIndividualSettingsActive,
    updateBoundaryTerritoryGroup: setBoundaryTerritoryGroup,
    onNumberOfRangesChange,
    onBoundaryTerritoryChange,
    onRecalculateRangesClick,
    onRestoreDefaultColors,
    isLoading,
    onSubmit,
  };
};
